import { useCallback, useMemo } from 'react';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { useMutationCreateProject } from '../../settings.queries';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';
import { EntityForm, NodeReference } from '../form.types';
import { ProjectSettingsForm } from './form';
import { ProjectSettingsFormOutput } from './form-output';
import { ProjectSettingsFormSchema } from './form-schema';
import { projectSettingsFormUISchema } from './ui-schema';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';

export function useCreateProjectForm(
  parentProjectID: string | undefined,
  parentNodeId: string | undefined,
  formData: ProjectSettingsForm | undefined
): EntityForm | null {
  // We expect to be able to find the parent project, becuase in order
  // to create a project, the user must have been able to select the parent
  // project from the tree.
  const parentProject = useQueryGetProject(parentProjectID);
  const projectMutation = useMutationCreateProject();
  const { hasAllFeatures: isExperimental } = useHasUIFeatures(['experimental']);

  // We will pass the parent project as the only option in the projectOptions array
  // so it can be rendered, but can't be changed.
  const projectOptions = useMemo(() => {
    return parentProject.data ? [parentProject.data] : [];
  }, [parentProject.data]);

  const defaultValues = useMemo(() => {
    return {
      parent: parentProjectID
    } as ProjectSettingsForm;
  }, [parentProjectID]);

  const initialFormOutput = useMemo(() => {
    return new ProjectSettingsFormOutput({});
  }, []);

  const currentFormOutput = useMemo(() => {
    return new ProjectSettingsFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  const formSchema = useMemo(() => {
    return ProjectSettingsFormSchema.FromProjectData(currentFormOutput, projectOptions, false, isExperimental);
  }, [currentFormOutput, projectOptions, isExperimental]);

  const onChangeInterceptor = useCallback((currentFormData: ProjectSettingsForm, newFormData: ProjectSettingsForm) => {
    const newFormDataCopy = { ...newFormData };
    // If the Country has changed, we need to clear the selected TimeZone
    if (currentFormData.timezone && currentFormData.country !== newFormData.country) {
      newFormDataCopy.timezone = undefined;
    }
    return newFormDataCopy;
  }, []);

  const onSubmit = useCallback(
    async (formOutput: ProjectSettingsFormOutput) => {
      const result = await projectMutation.mutateAsync({
        dto: ProjectSettingsFormOutput.ToCreateProjectDto(formOutput)
      });
      return {
        entityId: result.id,
        parentNodeId
      };
    },
    [parentNodeId, projectMutation]
  );

  const onSubmissionAction = useCallback((dispatch: SettingsActionDispatcher, result: void | NodeReference) => {
    if (!result) return;
    dispatch({
      type: 'SET_CURRENT_ACTION',
      payload: {
        type: 'PROJECT_CREATED'
      }
    });
  }, []);

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: parentProject.isLoading,
    title: 'Create Project',
    defaultValues,
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: projectSettingsFormUISchema,
    getOutput(formData) {
      return new ProjectSettingsFormOutput(formData as ProjectSettingsForm);
    },
    onChangeInterceptor,
    onSubmit,
    onSubmissionAction,
    isLocked: false,
    lockedMessage: null
  };
}
